import { useState } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { 
  Users, 
  CheckCircle,
  Info,
  UserCheck,
  HelpCircle,
  AlertCircle,
  Send
} from 'lucide-react'
import { HRDataCorrectionTicket } from '@/types/onboarding'

interface ManagerOrientationStepProps {
  directReportsCount: number
  directReportsList?: {
    employeeId: string
    fullName: string
    jobTitle: string
    email: string
  }[]
  employeeName: string           // Current user's name (for HR tickets)
  employeeId?: string            // Current user's personnel number
  employeeUserId?: number        // Database user ID for backend linking
  lineManagerName?: string       // Current user's line manager name (for display)
  onNext: () => void
  onBack: () => void
  onReportTeamIssue?: (ticket: string) => void
}

export default function ManagerOrientationStep({
  directReportsCount,
  directReportsList = [],
  employeeName,
  employeeId,
  employeeUserId,
  lineManagerName,
  onNext,
  onBack,
  onReportTeamIssue
}: ManagerOrientationStepProps) {
  const [teamConfirmed, setTeamConfirmed] = useState(false)
  const [reportingIssue, setReportingIssue] = useState(false)
  const [ticketSubmitted, setTicketSubmitted] = useState(false)
  
  // Form state for issue reporting
  const [selectedIssues, setSelectedIssues] = useState<string[]>([])
  const [issueDescription, setIssueDescription] = useState('')

  // Issue types for direct reports
  const issueTypes = [
    { id: 'missing-reports', label: 'Team members are missing from the list' },
    { id: 'extra-reports', label: 'People listed who don\'t report to me' },
    { id: 'wrong-details', label: 'Team member details are incorrect' },
    { id: 'wrong-manager', label: 'I should report to a different people leader' },
    { id: 'other', label: 'Other issue (specify in description)' }
  ]

  const handleIssueToggle = (issueId: string) => {
    setSelectedIssues(prev => 
      prev.includes(issueId) 
        ? prev.filter(i => i !== issueId)
        : [...prev, issueId]
    )
  }

  const handleSubmitTeamIssue = () => {
    const ticket: HRDataCorrectionTicket = {
      ticketId: `HR-${Date.now()}`,
      employeeUserId: employeeUserId,  // Database user ID for backend linking
      employeeId: employeeId || 'unknown',
      employeeName: employeeName,
      issueType: 'direct-reports',
      affectedFields: selectedIssues,
      userDescription: issueDescription,
      source: 'manager-orientation',
      description: `Manager reports issue with direct reports listing. Issues: ${selectedIssues.map(i => issueTypes.find(it => it.id === i)?.label).join(', ')}. Details: ${issueDescription || 'No additional details provided.'}`,
      status: 'pending',
      createdAt: new Date().toISOString()
    }
    
    if (onReportTeamIssue) {
      onReportTeamIssue(JSON.stringify(ticket))
    }
    setTicketSubmitted(true)
  }

  const canSubmitTicket = selectedIssues.length > 0

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-2"
      >
        <Badge className="bg-purple-100 text-purple-800 border-purple-200 mb-2">
          People Leader Setup
        </Badge>
        <h2 className="text-3xl font-larken font-bold text-heritage-blue">
          Confirm Your Team
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Before you begin, please verify that your direct reports are correctly listed below
        </p>
      </motion.div>

      {/* Main Content Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border-b">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-xl text-heritage-blue mb-1">
                  Your Direct Reports
                </CardTitle>
                <p className="text-sm text-muted-foreground font-normal">
                  You manage {directReportsCount} team {directReportsCount === 1 ? 'member' : 'members'}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Team Members List */}
            {!teamConfirmed && !reportingIssue && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border-2 border-purple-200 bg-purple-50">
                  <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center">
                    <UserCheck className="h-4 w-4 mr-2" />
                    Please Confirm Your Team Members
                  </h4>
                  <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                    {directReportsList.length > 0 ? (
                      directReportsList.map((report) => (
                        <div 
                          key={report.employeeId}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{report.fullName}</p>
                            <p className="text-xs text-gray-600">{report.jobTitle}</p>
                          </div>
                          <div className="text-xs text-gray-500">{report.email}</div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-purple-800 p-3 bg-white rounded-lg border border-purple-100">
                        No direct reports listed in system. If this is incorrect, please report below.
                      </p>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => setTeamConfirmed(true)}
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Confirm Team is Correct
                    </Button>
                    <Button
                      onClick={() => setReportingIssue(true)}
                      variant="outline"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                    >
                      Report Incorrect
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Team Confirmed Message */}
            {teamConfirmed && !reportingIssue && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-green-900">
                        Team Confirmed ✓
                      </p>
                      <p className="text-xs text-green-800">
                        You've confirmed your {directReportsCount} direct {directReportsCount === 1 ? 'report' : 'reports'}.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50">
                  <div className="flex items-start space-x-3">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-blue-900">
                        Your Dual Platform Access
                      </p>
                      <p className="text-xs text-blue-800">
                        You'll have access to both the <strong>Team Member Journey</strong> (for your own impact & growth) 
                        and the <strong>People Leader Hub</strong> (for your team). Switch between views using the navigation sidebar.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg border-2 border-purple-200 bg-purple-50">
                  <div className="flex items-start space-x-3">
                    <HelpCircle className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-purple-900">
                        Interactive Tour Available
                      </p>
                      <p className="text-xs text-purple-800">
                        When you first visit the People Leader Hub page, an interactive tour will guide you through 
                        all the features. You can also restart the tour anytime using the <strong>Tour</strong> button.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Incorrect Team Reporting - Form */}
            {reportingIssue && !ticketSubmitted && (
              <Card className="border-2 border-amber-200 bg-amber-50/50">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                    <AlertCircle className="h-5 w-5" />
                    Report Team Structure Issue
                  </CardTitle>
                  <p className="text-sm text-amber-700">
                    Please tell us what's incorrect so HR can update the records.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Issue Type Selection */}
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-amber-900">
                      What's the issue? <span className="text-red-500">*</span>
                    </Label>
                    <div className="space-y-2">
                      {issueTypes.map((issue) => (
                        <div 
                          key={issue.id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                            selectedIssues.includes(issue.id)
                              ? 'bg-amber-100 border-amber-400'
                              : 'bg-white border-gray-200 hover:border-amber-300'
                          }`}
                          onClick={() => handleIssueToggle(issue.id)}
                        >
                          <Checkbox
                            id={issue.id}
                            checked={selectedIssues.includes(issue.id)}
                            onCheckedChange={() => handleIssueToggle(issue.id)}
                            className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                          />
                          <Label 
                            htmlFor={issue.id} 
                            className="text-sm cursor-pointer flex-1"
                          >
                            {issue.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="teamIssueDescription" className="text-sm font-semibold text-amber-900">
                      Please provide details
                    </Label>
                    <Textarea
                      id="teamIssueDescription"
                      placeholder="E.g., John Smith should be on my team but isn't listed. Also, Jane Doe reports to Sarah Brown, not me."
                      value={issueDescription}
                      onChange={(e) => setIssueDescription(e.target.value)}
                      className="min-h-[100px] bg-white"
                    />
                    <p className="text-xs text-amber-700">
                      Include names and correct reporting structure if known.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-amber-200">
                    <Button
                      onClick={() => {
                        setReportingIssue(false)
                        setSelectedIssues([])
                        setIssueDescription('')
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmitTeamIssue}
                      disabled={!canSubmitTicket}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      size="sm"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Submit to HR
                    </Button>
                  </div>

                  {!canSubmitTicket && (
                    <p className="text-xs text-red-600 text-center">
                      Please select at least one issue type
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Ticket Submitted - Continue Message */}
            {ticketSubmitted && (
              <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-green-900">
                      Issue Reported Successfully
                    </p>
                    <p className="text-xs text-green-800">
                      HR will review your team structure and contact you to confirm corrections.
                    </p>
                    <p className="text-xs text-green-800">
                      <strong>You can continue with onboarding</strong> - the team data will be 
                      updated once confirmed with HR.
                    </p>
                    <Button
                      onClick={() => {
                        setReportingIssue(false)
                        setTeamConfirmed(true)
                      }}
                      size="sm"
                      className="mt-2 bg-green-600 hover:bg-green-700"
                    >
                      Continue with Onboarding
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Your Manager Info */}
      {lineManagerName && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-4 rounded-lg border border-gray-200 bg-gray-50"
        >
          <div className="flex items-center space-x-3">
            <UserCheck className="h-5 w-5 text-gray-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-600">
                Your people leader: <strong className="text-gray-900">{lineManagerName}</strong>
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4">
        <Button onClick={onBack} variant="outline">
          Back
        </Button>
        
        <Button 
          onClick={onNext}
          className="bg-purple-600 hover:bg-purple-700"
          disabled={!teamConfirmed && !ticketSubmitted}
        >
          Continue
          <CheckCircle className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
